import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { buffer, debounceTime, filter, fromEvent, map, merge, Observable, share, tap, zipWith } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ScannerService {
    private readonly window = inject(DOCUMENT).defaultView;

    private readonly keyup$ = fromEvent<KeyboardEvent>(this.window, 'keyup').pipe(
        share(),
    );
    private readonly character$ = this.keyup$.pipe(
        filter(event =>
            event.code.includes('Key') ||
            event.code.includes('Digit') ||
            event.code === 'Space' ||
            event.code === 'Minus',
        ),
        map(event => event.key),
    );
    private readonly closeBuffer$ = merge(
        this.keyup$.pipe(
            filter(event => event.key === 'Enter'),
            map(() => 'enter' as const),
        ),
        this.character$.pipe(
            debounceTime(100),
            map(() => 'stale' as const),
        ),
    ).pipe(
        share(),
    );

    /**
     * Emits serial numbers from scans (rapid key inputs ending with 'Enter').
     */
    readonly onScan$: Observable<string> = this.character$.pipe(
        buffer(this.closeBuffer$),
        zipWith(this.closeBuffer$),
        filter(([characters, reason]) => reason === 'enter' && characters.length > 0),
        map(([characters]) => characters.join('').toUpperCase()),
        tap(serialNumber => console.info('Scanned serial number:', serialNumber)),
        share(),
    );
}
